import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { API_URL } from "../config";

// Odhad času na jedno IČO (sekundy) – vyhledání v Sieblu + extrakce + návrat domů
const SEC_PER_ITEM = 14;

function normalizeIco(v) {
  return String(v || "").replace(/\D/g, "");
}

export default function PrmUpdateModal({ companies, userId, isDarkMode, onClose, onUpdated }) {
  // Portfolio → unikátní IČO (8 číslic). Bereme i firmy bez CA/PRM, ať je doplníme.
  const items = useMemo(() => {
    const map = new Map();
    (companies || []).forEach((c) => {
      const ico = normalizeIco(c.ico);
      if (ico.length < 1) return;
      if (!map.has(ico)) {
        map.set(ico, {
          ico,
          name: c.name || "",
          currentCa: c.ca || "",
          currentPrm: c.prm || "",
        });
      }
    });
    return Array.from(map.values());
  }, [companies]);

  const [extActive, setExtActive] = useState(false);
  const [extVersion, setExtVersion] = useState("");
  const [phase, setPhase] = useState("idle"); // idle | running | review | saving | done
  const [log, setLog] = useState([]);
  const [results, setResults] = useState({}); // ico -> { ca, prm, found }
  const [processed, setProcessed] = useState(0);
  const [startTs, setStartTs] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [saveMsg, setSaveMsg] = useState("");
  const [saveErr, setSaveErr] = useState("");

  const itemsRef = useRef(items);
  useEffect(() => { itemsRef.current = items; }, [items]);

  const addLog = useCallback((line) => {
    setLog((prev) => [...prev.slice(-200), line]);
  }, []);

  // Detekce extension + příjem zpráv
  useEffect(() => {
    if (window.__PB_EXT_ACTIVE__) {
      setExtActive(true);
      setExtVersion(window.__PB_EXT_VERSION__ || "");
    }
    const handler = (event) => {
      if (event.source !== window) return;
      if (event.data?.__pbPage) return;
      const msg = event.data;
      switch (msg?.type) {
        case "PB_EXTENSION_ACTIVE":
          setExtActive(true);
          if (msg.version) setExtVersion(msg.version);
          break;
        case "PB_PROGRESS":
          if (msg.message) addLog(msg.message);
          break;
        case "PRM_BATCH_ITEM": {
          const ico = normalizeIco(msg.ico);
          setResults((prev) => ({
            ...prev,
            [ico]: { ca: msg.ca || "", prm: msg.prm || "", found: !!msg.found },
          }));
          setProcessed((p) => p + 1);
          break;
        }
        case "PRM_BATCH_DONE":
          setPhase("review");
          addLog("🏁 Hotovo – zkontrolujte změny a potvrďte uložení.");
          break;
        default:
          break;
      }
    };
    window.addEventListener("message", handler);
    window.postMessage({ type: "PB_PING" }, "*");
    return () => window.removeEventListener("message", handler);
  }, [addLog]);

  // Tikání hodin pro odhad/uplynulý čas
  useEffect(() => {
    if (phase !== "running") return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [phase]);

  // Reset stavu v extension při zavření
  useEffect(() => {
    return () => { window.postMessage({ type: "PB_RESET" }, "*"); };
  }, []);

  const startBatch = useCallback(() => {
    if (!extActive || items.length === 0) return;
    setPhase("running");
    setLog([]);
    setResults({});
    setProcessed(0);
    setStartTs(Date.now());
    setNow(Date.now());
    setSaveMsg("");
    setSaveErr("");
    addLog(`▶ Spouštím aktualizaci ${items.length} IČO...`);
    window.postMessage({ type: "PB_RESET" }, "*");
    setTimeout(() => {
      window.postMessage({
        type: "PRM_BATCH_START",
        items: items.map((i) => ({ ico: i.ico })),
      }, "*");
    }, 120);
  }, [extActive, items, addLog]);

  // Změny k uložení: jen řádky, kde se reálně mění CA nebo PRM (a hodnota není prázdná)
  const changes = useMemo(() => {
    return items
      .map((it) => {
        const r = results[it.ico];
        if (!r) return null;
        const newCa = (r.ca || "").trim();
        const newPrm = (r.prm || "").trim();
        const caChanged = newCa && newCa !== (it.currentCa || "").trim();
        const prmChanged = newPrm && newPrm !== (it.currentPrm || "").trim();
        if (!caChanged && !prmChanged) return null;
        return {
          ...it,
          newCa: caChanged ? newCa : it.currentCa,
          newPrm: prmChanged ? newPrm : it.currentPrm,
          caChanged,
          prmChanged,
          found: r.found,
        };
      })
      .filter(Boolean);
  }, [items, results]);

  const notFoundCount = useMemo(
    () => Object.values(results).filter((r) => !r.found).length,
    [results]
  );

  const handleSave = useCallback(async () => {
    if (changes.length === 0) return;
    setPhase("saving");
    setSaveErr("");
    setSaveMsg("");
    try {
      const updates = changes.map((c) => ({
        ico: c.ico,
        ...(c.caChanged ? { ca: c.newCa } : {}),
        ...(c.prmChanged ? { prm_vodafone: c.newPrm } : {}),
      }));
      const res = await fetch(`${API_URL}/companies/bulk-ca-update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(userId ? { "X-User-Id": userId } : {}),
        },
        body: JSON.stringify({ updates }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json().catch(() => ({}));
      setSaveMsg(`Uloženo ${data.updatedCount ?? updates.length} změn.`);
      setPhase("done");
      if (onUpdated) onUpdated();
    } catch (e) {
      setSaveErr(`Uložení selhalo: ${e.message}`);
      setPhase("review");
    }
  }, [changes, userId, onUpdated]);

  // Odhad / uplynulý čas
  const totalSec = items.length * SEC_PER_ITEM;
  const elapsedSec = startTs ? Math.floor((now - startTs) / 1000) : 0;
  const remainingSec = Math.max(0, (items.length - processed) * SEC_PER_ITEM);
  const fmt = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return m > 0 ? `${m} min ${sec}s` : `${sec}s`;
  };

  const closeAll = () => {
    window.postMessage({ type: "PB_RESET" }, "*");
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-[130] p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-[96vw] max-w-4xl max-h-[92vh] flex flex-col">
        {/* Header */}
        <div className={`px-5 py-3 rounded-t-xl flex items-center justify-between flex-shrink-0 ${extActive ? "bg-indigo-700" : "bg-gray-600"}`}>
          <div className="flex items-center gap-3 text-white">
            <span className="text-lg">🔄</span>
            <div>
              <h3 className="font-bold text-base leading-tight">Aktualizace PRM</h3>
              <p className="text-xs text-indigo-200">
                {extActive
                  ? `${items.length} IČO v portfoliu`
                  : "Extension není nainstalována – návod níže"}
              </p>
            </div>
          </div>
          <button onClick={closeAll} className="p-1 hover:bg-white/20 rounded transition text-white">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5">
          {!extActive ? (
            /* ── Návod na instalaci ───────────────────────────────── */
            <div className="bg-amber-50 dark:bg-amber-900/10 rounded-lg p-4">
              <details className="group" open>
                <summary className="flex items-start gap-3 cursor-pointer list-none select-none">
                  <span className="text-3xl">🧩</span>
                  <div className="flex-1">
                    <p className="text-base font-bold text-amber-800 dark:text-amber-300 flex items-center gap-2">
                      Nainstalujte rozšíření pro hromadnou aktualizaci
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 transition-transform group-open:rotate-180 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-400">
                      Rozšíření prochází Siebel a aktualizuje PRM (Vlastník) a CA (Číslo zákazníka) automaticky.
                    </p>
                  </div>
                </summary>
                <ol className="text-sm text-amber-900 dark:text-amber-200 space-y-2 list-decimal pl-5 mt-3">
                  <li>
                    Stáhněte si rozšíření:
                    <a href="/pb-extension.zip" download="pb-extension.zip"
                      className="inline-block ml-2 px-3 py-1 rounded-lg bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold transition align-middle">
                      📦 Stáhnout pb-extension.zip
                    </a>
                  </li>
                  <li>Rozbalte stažený ZIP do trvalé složky (např. <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">Dokumenty/pb-extension</code>) – složku nemažte.</li>
                  <li>V Chrome/Edge otevřete stránku <code className="bg-amber-100 dark:bg-amber-900/40 px-1 rounded">chrome://extensions</code>.</li>
                  <li>Vpravo nahoře zapněte <strong>Režim pro vývojáře</strong> (Developer mode).</li>
                  <li>Klikněte na <strong>Načíst rozbalené</strong> (Load unpacked) a vyberte rozbalenou složku.</li>
                  <li>Rozšíření se objeví v seznamu – ujistěte se, že je <strong>zapnuté</strong>.</li>
                  <li><strong>Obnovte tuto stránku CRM</strong> (F5). Po obnovení se nahoře zobrazí „Aktualizace PRM" s počtem IČO.</li>
                </ol>
              </details>
            </div>
          ) : phase === "idle" ? (
            /* ── Úvod / spuštění ──────────────────────────────────── */
            <div className="text-center py-6">
              <div className="text-5xl mb-3">🔄</div>
              <p className="text-lg font-bold text-gray-800 dark:text-white mb-1">
                Aktualizovat PRM a CA pro {items.length} IČO
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4 max-w-lg mx-auto">
                Systém postupně vyhledá každé IČO ve vašem portfoliu v Sieblu a načte aktuální
                <strong> PRM Vodafone (Vlastník)</strong> a <strong>CA (Číslo zákazníka)</strong>.
                Na konci uvidíte přehled změn a teprve po potvrzení se uloží. Nic jiného se nezmění.
              </p>
              <div className="inline-block text-sm text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg px-4 py-2 mb-5">
                ⏱️ Odhadovaný čas: <strong>~{fmt(totalSec)}</strong> ({SEC_PER_ITEM}s / IČO)
              </div>
              <div>
                <button
                  onClick={startBatch}
                  disabled={items.length === 0}
                  className="px-6 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white rounded-xl font-semibold text-sm transition"
                >
                  ▶ Spustit aktualizaci
                </button>
              </div>
              <p className="text-[11px] text-gray-400 mt-3">
                Otevře se okno Sieblu. Nezavírejte ho, dokud aktualizace neskončí.
              </p>
            </div>
          ) : phase === "running" ? (
            /* ── Průběh ───────────────────────────────────────────── */
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-semibold text-gray-700 dark:text-gray-200">
                  Zpracováno {processed} / {items.length}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  ⏱️ Uplynulo {fmt(elapsedSec)} · zbývá ~{fmt(remainingSec)}
                </p>
              </div>
              <div className="w-full h-3 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden mb-4">
                <div
                  className="h-full bg-indigo-500 transition-all duration-500"
                  style={{ width: `${items.length ? Math.round((processed / items.length) * 100) : 0}%` }}
                />
              </div>
              <div className="bg-gray-50 dark:bg-gray-900 rounded-lg p-3 max-h-72 overflow-y-auto border border-gray-200 dark:border-gray-700">
                {log.length === 0 ? (
                  <p className="text-xs text-gray-400">Startuji...</p>
                ) : (
                  log.slice().reverse().map((line, i) => (
                    <p key={i} className="text-[12px] font-mono text-gray-600 dark:text-gray-300 leading-relaxed">{line}</p>
                  ))
                )}
              </div>
            </div>
          ) : (
            /* ── Kontrola změn + potvrzení (review / saving / done) ── */
            <div>
              {phase === "done" ? (
                <div className="text-center py-4 mb-2">
                  <div className="text-4xl mb-2">✅</div>
                  <p className="text-lg font-bold text-green-600 dark:text-green-400">{saveMsg || "Uloženo."}</p>
                </div>
              ) : (
                <div className="mb-3">
                  <p className="text-sm font-semibold text-gray-800 dark:text-white">
                    Kontrola změn – {changes.length} {changes.length === 1 ? "změna" : changes.length >= 2 && changes.length <= 4 ? "změny" : "změn"}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Zkontrolujte změny a potvrďte uložení. Uloží se <strong>pouze PRM a CA</strong> u řádků níže.
                    {notFoundCount > 0 && ` (${notFoundCount} IČO bez výsledku v Sieblu – přeskočeno)`}
                  </p>
                </div>
              )}

              {changes.length === 0 ? (
                <div className="text-center text-sm text-gray-500 dark:text-gray-400 py-6">
                  Žádné změny k uložení – všechny hodnoty už odpovídají Sieblu.
                </div>
              ) : (
                <div className="overflow-x-auto border border-gray-200 dark:border-gray-700 rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                      <tr>
                        <th className="text-left px-3 py-2 font-semibold">IČO</th>
                        <th className="text-left px-3 py-2 font-semibold">Název</th>
                        <th className="text-left px-3 py-2 font-semibold">CA (staré → nové)</th>
                        <th className="text-left px-3 py-2 font-semibold">PRM (staré → nové)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {changes.map((c) => (
                        <tr key={c.ico} className="border-t border-gray-100 dark:border-gray-700">
                          <td className="px-3 py-2 font-mono text-gray-700 dark:text-gray-200">{c.ico}</td>
                          <td className="px-3 py-2 text-gray-600 dark:text-gray-300 truncate max-w-[180px]">{c.name}</td>
                          <td className="px-3 py-2 font-mono">
                            {c.caChanged ? (
                              <span>
                                <span className="text-gray-400 line-through">{c.currentCa || "—"}</span>
                                <span className="text-green-600 dark:text-green-400 font-bold"> → {c.newCa}</span>
                              </span>
                            ) : (
                              <span className="text-gray-400">{c.currentCa || "—"}</span>
                            )}
                          </td>
                          <td className="px-3 py-2 font-mono">
                            {c.prmChanged ? (
                              <span>
                                <span className="text-gray-400 line-through">{c.currentPrm || "—"}</span>
                                <span className="text-green-600 dark:text-green-400 font-bold"> → {c.newPrm}</span>
                              </span>
                            ) : (
                              <span className="text-gray-400">{c.currentPrm || "—"}</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {saveErr && (
                <div className="mt-3 text-sm rounded-lg px-3 py-2 bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200 border border-red-300 dark:border-red-700">
                  ❌ {saveErr}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {extActive && (phase === "review" || phase === "saving" || phase === "done") && (
          <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3 bg-gray-50 dark:bg-gray-800 rounded-b-xl flex-shrink-0">
            {phase === "done" ? (
              <button
                onClick={closeAll}
                className="px-5 py-2 rounded-lg text-sm font-semibold bg-indigo-600 hover:bg-indigo-700 text-white transition-colors"
              >
                Zavřít
              </button>
            ) : (
              <>
                <button
                  onClick={closeAll}
                  disabled={phase === "saving"}
                  className="px-5 py-2 rounded-lg text-sm font-semibold border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors disabled:opacity-40"
                >
                  Zrušit
                </button>
                <button
                  onClick={handleSave}
                  disabled={phase === "saving" || changes.length === 0}
                  className={`px-5 py-2 rounded-lg text-sm font-semibold transition-colors ${
                    phase === "saving" || changes.length === 0
                      ? "bg-gray-400 text-white cursor-not-allowed"
                      : "bg-green-600 hover:bg-green-700 text-white"
                  }`}
                >
                  {phase === "saving" ? "Ukládám..." : `💾 Potvrdit a uložit (${changes.length})`}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
